import React, { useEffect, useRef, useState } from 'react';
import { portal } from '../lib/api';
import { useAuth } from '../context/AuthContext';

/**
 * Google's own sign-in button.
 *
 * Rendered by Google rather than restyled by us: their branding rules require
 * it, and a hand-built lookalike is the thing most likely to be mistaken for a
 * phishing prompt.
 *
 * The script is fetched only when this component mounts, so a visitor who never
 * opens the sign-in dialog never loads anything from Google — which is the
 * difference between offering Google sign-in and embedding Google on every page.
 *
 * The client id comes from the server (/portal/google/config), not from a build
 * variable, so it can change without rebuilding the bundle.
 */
let scriptPromise = null;
let clientIdPromise = null;

/** Fetched once per page load, not once per mount — the id never changes mid-session. */
function loadClientId() {
  if (!clientIdPromise) {
    clientIdPromise = portal.googleConfig()
      .then((cfg) => cfg.client_id || '')
      .catch(() => { clientIdPromise = null; return ''; });
  }
  return clientIdPromise;
}

function loadGis() {
  if (window.google?.accounts?.id) return Promise.resolve(true);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((done) => {
    const el = document.createElement('script');
    el.src = 'https://accounts.google.com/gsi/client';
    el.async = true;
    el.defer = true;
    el.onload = () => done(true);
    el.onerror = () => { scriptPromise = null; done(false); };
    document.head.appendChild(el);
  });
  return scriptPromise;
}

export default function GoogleButton({ onDone, onError, text = 'continue_with' }) {
  const holder = useRef(null);
  const { setSession } = useAuth();
  const [state, setState] = useState('loading'); // loading | ready | unavailable

  // The callers pass inline arrow functions, so these change identity on every
  // parent render. Held in refs, the effect below keeps a stable dependency
  // list and runs once per mount — otherwise each re-render cancels the
  // in-flight run and restarts it, and the button never finishes rendering.
  const inFlight = useRef(false);
  const onDoneRef = useRef(onDone);
  const onErrorRef = useRef(onError);
  onDoneRef.current = onDone;
  onErrorRef.current = onError;

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const clientId = await loadClientId();
      if (cancelled) return;
      if (!clientId) { setState('unavailable'); return; }

      const ok = await loadGis();
      if (cancelled) return;
      if (!ok || !window.google?.accounts?.id) { setState('unavailable'); return; }

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (res) => {
          // GIS can deliver the credential more than once (a second click
          // while the first request is slow, One Tap plus the button). One
          // sign-in at a time: a duplicate would race the server's session
          // rotation and 401 the next request.
          if (inFlight.current) return;
          inFlight.current = true;
          try {
            const r = await portal.google(res.credential);
            // Same shape as login/signup, so the session is established
            // identically however somebody got here.
            setSession(r.user, r.portal_key);
            onDoneRef.current?.(r);
          } catch (e) {
            onErrorRef.current?.(
              e?.response?.data?.detail || 'That Google sign-in did not work.'
            );
          } finally {
            inFlight.current = false;
          }
        },
      });

      if (holder.current) {
        holder.current.innerHTML = '';
        // GIS only honours widths between 200 and 400, so on a panel wider
        // than 400 the button always comes out short of the form fields —
        // the holder is a centering flex row so it sits centred rather than
        // hanging off the left edge.
        const w = Math.min(400, Math.max(200, holder.current.offsetWidth || 320));
        window.google.accounts.id.renderButton(holder.current, {
          theme: 'outline',
          size: 'large',
          shape: 'rectangular',
          text,
          width: w,
        });
      }
      setState('ready');
    })();

    return () => { cancelled = true; };
  }, [setSession, text]);

  if (state === 'unavailable') return null;

  return (
    <div>
      <div ref={holder} style={{ minHeight: 44, display: 'flex', justifyContent: 'center' }} />
      {state === 'loading' && (
        <div className="text-[0.85rem] text-[var(--text-4)] py-3">Loading Google…</div>
      )}
    </div>
  );
}
